import Cookies from "js-cookie";
import { mapStores } from "pinia";
import { useAppStateStore } from "@/stores/AppStateStore";
import { useAudienceExplorerSearchStore } from "@/stores/AudienceExplorerSearchStore";
import { useCampaignEffectivenessStore } from "@/stores/CampaignEffectivenessStore";
import { useMetaAudienceStore } from "@/stores/MetaAudienceStore";
import { useResearchStore } from "@/stores/ResearchStore";
import { useResearchStudioStore } from "@/stores/ResearchStudioStore";
import { useThemeStore } from "@/stores/ThemeStore";
import { useUserActivityStore } from "@/stores/UserActivityStore";
import { useUserStore } from "@/stores/UserStore";
import { useGeographyStore } from "@/stores/GeographyStore";
import { useImpersonation } from "@/composables/useImpersonation";

export const userTierMixin = {
  computed: {
    ...mapStores(
      useAppStateStore,
      useAudienceExplorerSearchStore,
      useCampaignEffectivenessStore,
      useMetaAudienceStore,
      useResearchStore,
      useResearchStudioStore,
      useThemeStore,
      useUserActivityStore,
      useUserStore,
      useGeographyStore,
    ),
    currentUser() {
      return this.userStore.currentUser;
    },
    currentOrg() {
      return this.userStore.currentOrg;
    },
    currentAppClient() {
      return this.userStore.currentAppClient;
    },
    platformTheme() {
      return this.themeStore.activeTheme?.name;
    },
    themeBrand() {
      return this.themeStore.brand;
    },
    loggedIn() {
      return !!this.currentUser && !!Cookies.get("access_token");
    },
    paidProductsPermissions() {
      return {
        "TV Ratings": this.hasTvRatings,
        "Cable Ranker": this.hasCableRanker,
        "Political Paid Media": this.hasPoliticalPaidMedia,
        "Reach and Frequency": this.hasReachFreq,
      };
    },
    requiresTmmsSetup() {
      return { "Cable Ranker": true };
    },
    metaAudiences() {
      if (this.currentAppClient) {
        return [...(this.currentUser.meta_audiences ?? [])].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
      } else {
        return this.metaAudienceStore.metaAudiencesSorted;
      }
    },
    orgAudienceLimits() {
      return this.currentOrg?.audience_allowance
        ? this.currentOrg.meta_audiences
        : false;
    },
    userHasOrgs() {
      return !!(
        this.currentUser &&
        Array.isArray(this.currentUser.orgs) &&
        this.currentUser.orgs.length
      );
    },
    hasReachFreq() {
      return this.currentUser && this.$hasProduct("reach_&_frequency");
    },
    hasCableRanker() {
      return this.currentUser && this.$hasProduct("cable_ranker");
    },
    hasAudienceBuilder() {
      return this.currentUser && this.$hasProduct("audience_builder");
    },
    hasTvRatings() {
      return this.currentUser && this.$hasProduct("tv_ratings");
    },
    hasPoliticalPaidMedia() {
      return this.currentUser && this.$hasProduct("political_paid_media");
    },
    hasCampaignEffectiveness() {
      return (
        this.currentUser &&
        this.campaignEffectivenessStore.campaigns?.length > 0
      );
    },
    hasResearchStudio() {
      return this.currentUser && this.researchStudioStore.surveys?.length > 0;
    },
    hasRateCard() {
      return this.currentUser && this.$hasProduct("rate_card");
    },
    hasOnlyFileSharing() {
      return (
        this.currentUser && this.$can("file_sharing_only") && !this.internalUser
      );
    },
    internalUser() {
      return this.currentUser && this.$can("internal_user");
    },
    userAudiences() {
      if (this.currentAppClient) {
        return this.clientUserAudiences;
      } else {
        return this.orgUserAudiences;
      }
    },
    clientUserAudiences() {
      let userAudiences = this.metaAudiences
        .filter(
          (a) => a.app_client_id === this.currentAppClient.id && !a.is_personal,
        )
        .concat(this.metaAudiences.filter((a) => a.premium));

      if (this.currentUser) {
        userAudiences = userAudiences.concat(
          this.metaAudiences.filter(
            (a) =>
              !a.client_id &&
              (a.created_by_id === this.currentUser.id ||
                a.shared_with?.includes(this.currentUser.email)),
          ),
        );
      }

      const dupes = new Set();
      userAudiences = userAudiences.filter((a) => {
        if (!dupes.has(a.id)) {
          dupes.add(a.id);
          return true;
        }
        return false;
      });
      return userAudiences.sort((a, b) => a.name.localeCompare(b.name));
    },
    orgUserAudiences() {
      let clientAudiences = [];
      this.currentOrg?.clients.forEach((client) => {
        this.metaAudiences.forEach((aud) => {
          if (aud.client_id === client.id) {
            clientAudiences.push(aud);
          }
        });
      });

      let userAudiences = clientAudiences.concat(
        this.metaAudiences.filter((a) => a.premium),
      );

      if (this.orgAudienceLimits) {
        userAudiences.push(...this.orgAudienceLimits);
      }
      // add user created audiences & audiences shared with current user
      if (this.currentUser) {
        userAudiences = userAudiences.concat(
          this.metaAudiences.filter(
            (a) =>
              !a.client_id &&
              (a.created_by_id === this.currentUser.id ||
                a.shared_with.includes(this.currentUser.email)),
          ),
        );
      }
      const dupes = new Set();
      userAudiences = userAudiences.filter((a) => {
        if (!dupes.has(a.id)) {
          dupes.add(a.id);
          return true;
        }
        return false;
      });
      return userAudiences.sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  methods: {
    initializeUserSettings() {
      if (this.userStore.initializing || this.userStore.initialized) {
        return Promise.resolve();
      }
      this.userStore.initializing = true;
      const requests = [
        this.userStore.initializePendo(),
        this.audienceExplorerSearchStore.loadDefaultAudience(),
        this.metaAudienceStore.getHighLowAudiences("high_streaming"),
        this.metaAudienceStore.getHighLowAudiences("low_streaming"),
        this.metaAudienceStore.getHighLowAudiences("high_linear_TV"),
        this.metaAudienceStore.getHighLowAudiences("low_linear_TV"),
        this.metaAudienceStore.getHighLowAudiences(
          "low_linear_/_high_streaming",
        ),
        this.metaAudienceStore.getHighLowAudiences(
          "high_linear_/_high_streaming",
        ),
        this.metaAudienceStore.getHighLowAudiences(
          "low_linear_/_low_streaming",
        ),
        this.metaAudienceStore.getHighLowAudiences(
          "high_linear_/_low_streaming",
        ),
        this.campaignEffectivenessStore.listCampaigns(),
        this.geographyStore.loadStates(),
        // TODO: remove when we deprecate ResearchStudio
        this.researchStudioStore.listSurveys(),

        this.researchStore.listSurveys(),
        this.researchStore.listKpis(),
        this.researchStore.listCampaigns(),
      ];

      if (this.currentOrg) {
        requests.push(this.metaAudienceStore.getMetaAudiences());
      }

      return Promise.all(requests)
        .then(() => {
          this.userStore.initialized = true;
        })
        .finally(() => {
          this.userStore.initializing = false;
        });
    },
    impersonateUser(user, org) {
      return useImpersonation().impersonateUser(user, org);
    },
  },
};
